/**
 * AutoMe AI — chat modal UI with welcome screen + multi-conversation support.
 *
 * UX flow:
 *
 *   open() → WELCOME screen (default)
 *     ├─ "+ שיחה חדשה"             → creates new conv, enters CHAT mode
 *     ├─ click on a previous conv   → loads that conv, enters CHAT mode
 *     └─ "✕" header button          → closes modal
 *
 *   CHAT mode
 *     ├─ "← שיחות" (header button)  → back to WELCOME (does NOT close modal)
 *     ├─ "+ חדשה" (header button)   → creates a new conv, stays in CHAT
 *     ├─ "✕"                         → closes modal
 *     └─ submit message              → ask service, persist, render reply
 *
 *   open(preset) — when called with a preset prompt (e.g. from the daily
 *   brief contextual chip), we SKIP the welcome screen and go straight
 *   into a new conversation with the input pre-filled. The user's intent
 *   is already a question, not "browse history".
 */

import * as service from './aiChat.service.js';
import { escape as esc } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';
import { iconHtml } from './icon.js';
import { prepareImage } from '../../shared/imageUtil.js';

const log = createLogger('ai.view');

const OVERLAY_ID = 'aiChatOverlay';

/** How many conversations to show on the welcome screen before "see all". */
const WELCOME_PREVIEW_COUNT = 5;

const SUGGESTED_PROMPTS = [
  '💰 כמה הרכב שלי שווה?',
  '📅 מתי לחדש את הביטוח?',
  '🔧 איפה הכי כדאי לעשות טסט?',
  '⚖️ מה החוק על שיחה ביד בנהיגה?',
];

/** @type {Array<{role:'user'|'assistant',content:string}>} */
let messages = [];
/** @type {Array<import('./aiChat.service.js').ConversationMeta>} */
let conversations = [];
/** @type {string|null} */
let activeConvId = null;
let isSending = false;
/** Current view mode — controls which screen is rendered. */
let viewMode = /** @type {'welcome'|'chat'} */ ('welcome');
/** When `true`, the welcome screen shows all conversations instead of the top 5. */
let welcomeShowAll = false;
/**
 * "Draft" chat — the user clicked "+ New chat" (or opened with a preset)
 * but has not yet sent their first message. We DON'T persist anything in
 * RTDB until the first send; otherwise empty conversations would pile up
 * in the conversations list every time the user just peeks at the chat.
 * Cleared when the first message is sent (createConversation in onSubmit).
 */
let isDraft = false;

/**
 * Photo currently attached to the next message (Photo Diagnostic).
 * Cleared after submit. Only one image at a time is supported by the UI
 * (the server accepts up to 3 — easy to expand later).
 * @type {import('../../shared/imageUtil.js').PreparedImage | null}
 */
let pendingImage = null;

/**
 * Open the chat modal. Idempotent.
 *
 * @param {string | null} [preset]  Optional prompt text. If provided, the
 *   welcome screen is skipped and a new conversation is started with the
 *   input pre-filled — the user's intent is already a question. If omitted,
 *   the welcome screen is shown so the user can pick "new" or "continue".
 * @param {{ autoTriggerPhoto?: boolean }} [opts]  When `autoTriggerPhoto`
 *   is true (from the Photo Diagnostic intro CTA) the welcome screen is
 *   skipped, a new draft chat is opened, and the file picker is invoked
 *   automatically so the user lands directly on the camera/gallery.
 */
export async function open(preset, opts) {
  const overlay = ensureModal();
  document.body.appendChild(overlay);
  overlay.classList.add('open');

  const autoPhoto = !!(opts && opts.autoTriggerPhoto);
  if (autoPhoto) {
    // Photo diagnostic short-circuit — drop straight into an empty draft
    // chat and pop the file picker. The user came here from the intro
    // modal and just wants to take a photo.
    activeConvId = null;
    isDraft = true;
    messages = [];
    viewMode = 'chat';
    renderActiveView();
    setTimeout(() => {
      const photoInput = /** @type {HTMLInputElement | null} */ (document.getElementById('aiChatPhotoInput'));
      photoInput?.click();
    }, 250);
    return;
  }

  if (preset && typeof preset === 'string') {
    // Preset path — skip welcome, prepare a DRAFT new conversation (no
    // RTDB write yet) and pre-fill the input. The conversation only
    // materializes when the user actually sends a message.
    activeConvId = null;
    isDraft = true;
    messages = [];
    viewMode = 'chat';
    renderActiveView();
    setTimeout(() => {
      const input = /** @type {HTMLInputElement | null} */ (document.getElementById('aiChatInput'));
      if (input) { input.value = preset; input.focus(); }
    }, 50);
    return;
  }

  // Default path — show the welcome screen.
  viewMode = 'welcome';
  welcomeShowAll = false;
  activeConvId = null;
  isDraft = false;
  messages = [];
  renderActiveView();
  void refreshConversations().then(() => renderWelcome());
}

export function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  overlay.classList.remove('open');
}

// ====================================================================
// DOM scaffolding
// ====================================================================

function ensureModal() {
  let ov = document.getElementById(OVERLAY_ID);
  if (ov) return ov;
  ov = document.createElement('div');
  ov.id = OVERLAY_ID;
  ov.className = 'ai-chat-overlay';
  ov.innerHTML = `
    <aside class="ai-chat-modal" role="dialog" aria-modal="true" aria-labelledby="aiChatTitle">
      <header class="ai-chat-header" id="aiChatHeader"></header>
      <div class="ai-chat-body" id="aiChatBody"></div>
      <footer class="ai-chat-footer" id="aiChatFooter" hidden>
        <div class="ai-chat-quota" id="aiChatQuota"></div>
        <div class="ai-chat-pending-image" id="aiChatPendingImage" hidden></div>
        <form class="ai-chat-form" id="aiChatForm">
          <button type="button" class="ai-chat-photo" id="aiChatPhotoBtn"
                  aria-label="צרף תמונה לאבחון" title="צלם תקלה בדשבורד / נזק / נזילה לאבחון">📸</button>
          <input type="file" id="aiChatPhotoInput" accept="image/*"
                 capture="environment" hidden>
          <input type="text" id="aiChatInput" class="ai-chat-input"
                 placeholder="שאל אותי על הרכב שלך..."
                 autocomplete="off" maxlength="1500" />
          <button type="submit" class="ai-chat-send" aria-label="שלח">↑</button>
        </form>
      </footer>
    </aside>
  `;
  // Backdrop click closes (only if clicked on overlay itself, not the modal)
  ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
  ov.querySelector('#aiChatForm')?.addEventListener('submit', onSubmit);
  ov.querySelector('#aiChatPhotoBtn')?.addEventListener('click', () => {
    const input = /** @type {HTMLInputElement | null} */ (document.getElementById('aiChatPhotoInput'));
    input?.click();
  });
  ov.querySelector('#aiChatPhotoInput')?.addEventListener('change', onPhotoPicked);
  return ov;
}

/**
 * Render whichever view mode is active. Header + body + footer visibility
 * are all driven from here so we don't accumulate stale handlers.
 */
function renderActiveView() {
  renderHeader();
  const footer = document.getElementById('aiChatFooter');
  if (footer) footer.hidden = viewMode !== 'chat';
  if (viewMode === 'welcome') renderWelcome();
  else renderMessages();
}

function renderHeader() {
  const header = document.getElementById('aiChatHeader');
  if (!header) return;

  if (viewMode === 'welcome') {
    // Welcome screen — no back button, just the title + close.
    header.innerHTML = `
      <div class="ai-chat-spacer"></div>
      <div class="ai-chat-title-block">
        <span class="ai-chat-avatar">${iconHtml(40)}</span>
        <div>
          <div class="ai-chat-title" id="aiChatTitle">AutoMe AI</div>
          <div class="ai-chat-sub">העוזר האישי של הרכב שלך</div>
        </div>
      </div>
      <button type="button" class="ai-chat-close" aria-label="סגור">✕</button>
    `;
  } else {
    // Chat mode — header has a back-arrow to return to welcome.
    const active = conversations.find((c) => c.id === activeConvId);
    const titleText = active && active.title && active.title !== 'שיחה חדשה'
      ? active.title : 'שיחה חדשה';
    header.innerHTML = `
      <button type="button" class="ai-chat-back" aria-label="חזרה לשיחות" title="חזרה למסך השיחות">←</button>
      <div class="ai-chat-title-block">
        <span class="ai-chat-avatar">${iconHtml(40)}</span>
        <div>
          <div class="ai-chat-title" id="aiChatTitle">${esc(titleText)}</div>
          <div class="ai-chat-sub">AutoMe AI</div>
        </div>
      </div>
      <button type="button" class="ai-chat-close" aria-label="סגור">✕</button>
    `;
    header.querySelector('.ai-chat-back')?.addEventListener('click', backToWelcome);
  }
  header.querySelector('.ai-chat-close')?.addEventListener('click', close);
}

// ====================================================================
// Welcome screen
// ====================================================================

async function refreshConversations() {
  try {
    conversations = await service.listConversations();
  } catch (e) {
    log.warn('listConversations:', e);
    conversations = [];
  }
}

function renderWelcome() {
  const body = document.getElementById('aiChatBody');
  if (!body) return;
  body.classList.add('is-welcome');

  const showAll = welcomeShowAll;
  const visible = showAll ? conversations : conversations.slice(0, WELCOME_PREVIEW_COUNT);
  const hiddenCount = conversations.length - visible.length;

  body.innerHTML = `
    <div class="ai-welcome">
      <div class="ai-welcome-hero">
        <div class="ai-welcome-emoji">${iconHtml(96)}</div>
        <div class="ai-welcome-greeting">ברוכים השבים!</div>
        <div class="ai-welcome-sub">במה אוכל לעזור היום?</div>
      </div>

      <button type="button" class="ai-welcome-new-btn" id="aiWelcomeNewBtn">
        <span class="ai-welcome-new-icon">＋</span>
        <div class="ai-welcome-new-text">
          <div class="ai-welcome-new-title">שיחה חדשה</div>
          <div class="ai-welcome-new-sub">התחל לשאול שאלה חדשה</div>
        </div>
      </button>

      ${conversations.length === 0 ? renderWelcomeFirstTime() : renderWelcomePastConvs(visible, hiddenCount, showAll)}
    </div>
  `;

  body.querySelector('#aiWelcomeNewBtn')?.addEventListener('click', onNewConversation);

  // Past-conversation card clicks
  body.querySelectorAll('.ai-welcome-conv').forEach((el) => {
    const id = el.getAttribute('data-conv-id') || '';
    el.querySelector('.ai-welcome-conv-main')?.addEventListener('click', () => onSwitchConversation(id));
    el.querySelector('.ai-welcome-conv-del')?.addEventListener('click', (e) => {
      e.stopPropagation();
      onDeleteConversation(id);
    });
  });

  // "See all" / "Show less" toggle
  body.querySelector('#aiWelcomeSeeAll')?.addEventListener('click', () => {
    welcomeShowAll = !welcomeShowAll;
    renderWelcome();
  });

  // First-time suggestion chips
  body.querySelectorAll('[data-suggested-prompt]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const prompt = btn.getAttribute('data-suggested-prompt') || '';
      void startFromSuggestion(prompt);
    });
  });
}

function renderWelcomeFirstTime() {
  // No prior conversations — show suggestion chips to spark the first ask.
  return `
    <div class="ai-welcome-firsttime">
      <div class="ai-welcome-firsttime-title">או נסה אחת מהשאלות הפופולריות:</div>
      <div class="ai-welcome-suggestions">
        ${SUGGESTED_PROMPTS.map((p) => {
          const clean = p.replace(/^[\p{Emoji}‍️]+\s*/u, '').trim();
          return `
            <button type="button" class="ai-welcome-suggestion" data-suggested-prompt="${esc(clean)}">
              ${esc(p)}
            </button>
          `;
        }).join('')}
      </div>
    </div>
  `;
}

function renderWelcomePastConvs(visible, hiddenCount, showAll) {
  return `
    <div class="ai-welcome-divider"><span>או המשך שיחה קודמת</span></div>
    <div class="ai-welcome-convlist">
      ${visible.map((c) => {
        const when = formatWhen(c.updatedAt);
        return `
          <div class="ai-welcome-conv" data-conv-id="${esc(c.id)}">
            <button type="button" class="ai-welcome-conv-main">
              <div class="ai-welcome-conv-title">${esc(c.title || 'שיחה חדשה')}</div>
              <div class="ai-welcome-conv-meta">${c.messageCount} הודעות · ${esc(when)}</div>
            </button>
            <button type="button" class="ai-welcome-conv-del" aria-label="מחק שיחה" title="מחק שיחה">🗑️</button>
          </div>
        `;
      }).join('')}
      ${hiddenCount > 0 || showAll ? `
        <button type="button" class="ai-welcome-seeall" id="aiWelcomeSeeAll">
          ${showAll
            ? '◂ הצג רק אחרונות'
            : `ראה את כל השיחות (${conversations.length}) ◂`}
        </button>
      ` : ''}
    </div>
  `;
}

function formatWhen(ts) {
  if (!ts || !Number.isFinite(ts)) return '';
  const now = Date.now();
  const diff = now - ts;
  if (diff < 60_000) return 'עכשיו';
  if (diff < 3600_000) return `לפני ${Math.floor(diff / 60_000)} ד'`;
  const today = new Date(now).toDateString();
  const then = new Date(ts);
  if (then.toDateString() === today) {
    return `היום ${then.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
  }
  const yesterday = new Date(now - 86400_000).toDateString();
  if (then.toDateString() === yesterday) {
    return `אתמול ${then.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return then.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
}

// ====================================================================
// Chat view
// ====================================================================

async function loadAndRender() {
  const body = document.getElementById('aiChatBody');
  if (!body) return;
  body.classList.remove('is-welcome');
  body.innerHTML = `<div class="ai-chat-loading">⏳ טוען...</div>`;
  try {
    messages = await service.loadHistory(activeConvId || undefined);
  } catch (_) { messages = []; }
  renderMessages();
}

function renderMessages() {
  const body = document.getElementById('aiChatBody');
  if (!body) return;
  body.classList.remove('is-welcome');

  if (messages.length === 0) {
    // Empty conversation — invite the user to type. We DON'T show the
    // suggestion chips here anymore (those live on the welcome screen)
    // so the empty chat looks clean and focused.
    body.innerHTML = `
      <div class="ai-chat-empty-mini">
        <div class="ai-chat-empty-mini-emoji">💬</div>
        <div class="ai-chat-empty-mini-text">
          שאל אותי כל שאלה על הרכב שלך — שווי, חוקים, טיפים או חיסכון.
        </div>
      </div>
    `;
    // Auto-focus the input so the user can just start typing.
    setTimeout(() => {
      const input = /** @type {HTMLInputElement | null} */ (document.getElementById('aiChatInput'));
      input?.focus();
    }, 50);
    return;
  }

  body.innerHTML = messages.map(renderBubble).join('') +
    (isSending ? renderTyping() : '');
  body.scrollTop = body.scrollHeight;
}

function renderBubble(m) {
  const side = m.role === 'user' ? 'right' : 'left';
  const cls = m.role === 'user' ? 'ai-chat-bubble-user' : 'ai-chat-bubble-ai';
  return `
    <div class="ai-chat-row ai-chat-row-${side}">
      <div class="ai-chat-bubble ${cls}">${esc(m.content).replace(/\n/g, '<br>')}</div>
    </div>
  `;
}

function renderTyping() {
  return `
    <div class="ai-chat-row ai-chat-row-left">
      <div class="ai-chat-bubble ai-chat-bubble-ai ai-chat-typing">
        <span></span><span></span><span></span>
      </div>
    </div>
  `;
}

// ====================================================================
// Photo Diagnostic — image attachment flow
// ====================================================================

/**
 * Triggered when the user picks an image via the 📸 button. Downscales
 * and re-encodes the file via `prepareImage`, stores it in `pendingImage`,
 * and shows a preview thumbnail above the input. Submit will include it.
 */
async function onPhotoPicked(e) {
  const input = /** @type {HTMLInputElement} */ (e.currentTarget || e.target);
  const file = input.files && input.files[0];
  // Reset the input so picking the SAME file twice still fires `change`.
  input.value = '';
  if (!file) return;
  if (!file.type.startsWith('image/')) {
    log.warn('not an image:', file.type);
    return;
  }

  try {
    pendingImage = await prepareImage(file);
  } catch (err) {
    log.warn('prepareImage failed:', err);
    pendingImage = null;
    return;
  }
  renderPendingImagePreview();
  // Make sure we're in chat mode so the user sees the preview right away.
  if (viewMode !== 'chat') {
    activeConvId = null;
    isDraft = true;
    messages = [];
    viewMode = 'chat';
    renderActiveView();
    renderPendingImagePreview();
  }
  const textInput = /** @type {HTMLInputElement | null} */ (document.getElementById('aiChatInput'));
  textInput?.focus();
}

function renderPendingImagePreview() {
  const slot = document.getElementById('aiChatPendingImage');
  if (!slot) return;
  if (!pendingImage) {
    slot.hidden = true;
    slot.innerHTML = '';
    return;
  }
  slot.hidden = false;
  slot.innerHTML = `
    <img src="${esc(pendingImage.previewUrl)}" alt="תמונה לאבחון" class="ai-chat-pending-thumb">
    <div class="ai-chat-pending-meta">
      <div class="ai-chat-pending-label">📸 צורף לאבחון</div>
      <div class="ai-chat-pending-hint">תוסיף שאלה ספציפית או שלח ריק לאבחון אוטומטי</div>
    </div>
    <button type="button" class="ai-chat-pending-remove" aria-label="הסר תמונה">✕</button>
  `;
  slot.querySelector('.ai-chat-pending-remove')?.addEventListener('click', () => {
    pendingImage = null;
    renderPendingImagePreview();
  });
}

// ====================================================================
// Navigation between views
// ====================================================================

function backToWelcome() {
  viewMode = 'welcome';
  welcomeShowAll = false;
  activeConvId = null;
  isDraft = false;
  messages = [];
  renderActiveView();
  void refreshConversations().then(() => renderWelcome());
}

async function onNewConversation() {
  // Don't write anything to RTDB yet — the conversation materializes on
  // the first message send (see onSubmit). This avoids piling up empty
  // conversations every time the user clicks "+ שיחה חדשה" and walks away.
  activeConvId = null;
  isDraft = true;
  messages = [];
  viewMode = 'chat';
  renderActiveView();
  setTimeout(() => {
    const input = /** @type {HTMLInputElement | null} */ (document.getElementById('aiChatInput'));
    input?.focus();
  }, 50);
}

async function onSwitchConversation(id) {
  if (!id) return;
  activeConvId = id;
  isDraft = false;
  try { await service.setActiveConvId(id); } catch (_) { /* ignore */ }
  viewMode = 'chat';
  renderActiveView();
  await loadAndRender();
}

async function onDeleteConversation(id) {
  if (!id) return;
  const conv = conversations.find((c) => c.id === id);
  const title = conv?.title || 'שיחה';
  if (!confirm(`למחוק את השיחה "${title}"?\n\nפעולה זו אינה הפיכה.`)) return;
  try { await service.deleteConversation(id); }
  catch (e) { log.warn('delete failed:', e); }
  await refreshConversations();
  renderWelcome();
}

/** Used when the first-time user clicks a suggestion chip on the welcome. */
async function startFromSuggestion(prompt) {
  await onNewConversation();
  setTimeout(() => {
    const input = /** @type {HTMLInputElement | null} */ (document.getElementById('aiChatInput'));
    if (!input) return;
    input.value = prompt;
    input.focus();
  }, 100);
}

// ====================================================================
// Submit handler
// ====================================================================

async function onSubmit(e) {
  e?.preventDefault?.();
  if (isSending) return;
  if (viewMode !== 'chat') return;  // can't submit from welcome screen
  const input = /** @type {HTMLInputElement | null} */ (document.getElementById('aiChatInput'));
  if (!input) return;
  const text = input.value.trim();
  // Allow empty text WHEN an image is attached (Photo Diagnostic).
  if (!text && !pendingImage) return;
  input.value = '';

  // Snapshot + clear the pending image BEFORE the async call so a new
  // photo attached mid-flight goes with the NEXT message, not this one.
  const imageForThisTurn = pendingImage;
  pendingImage = null;
  renderPendingImagePreview();

  // First-send lazy create: if this is a draft (user clicked "+ New" or
  // opened via preset), materialize the conversation in RTDB now.
  if (isDraft || !activeConvId) {
    try {
      const id = await service.createConversation();
      if (id) { activeConvId = id; isDraft = false; }
    } catch (e2) { log.warn('lazy createConversation failed:', e2); }
  }

  // In-memory message — annotate with 📷 if there was an image (matches
  // what's stored in RTDB by the service).
  const displayContent = imageForThisTurn
    ? (text ? `📷 ${text}` : '📷 [תמונה לאבחון]')
    : text;
  messages.push({ role: 'user', content: displayContent });
  isSending = true;
  renderMessages();

  try {
    const images = imageForThisTurn
      ? [{ mediaType: imageForThisTurn.mediaType, data: imageForThisTurn.data }]
      : undefined;
    const { answer, quotaLeft, convId } = await service.ask(
      text, messages.slice(0, -1), activeConvId || undefined, images,
    );
    if (convId) activeConvId = convId;
    messages.push({ role: 'assistant', content: answer });
    updateQuota(quotaLeft);
    // Re-render header so the dynamic title (derived from first user msg)
    // shows once the conversation gets a real title.
    renderHeader();
  } catch (err) {
    log.warn('ask failed:', err?.message);
    const msg = String(err?.message || '');
    const friendly = /resource-exhausted|מכסה/i.test(msg)
      ? 'הגעת למכסה היומית (20 שאלות). מכסה מתאפסת בחצות.'
      : 'מצטער, משהו השתבש. נסה שוב בעוד רגע.';
    messages.push({ role: 'assistant', content: friendly });
  } finally {
    isSending = false;
    renderMessages();
  }
}

function updateQuota(left) {
  const el = document.getElementById('aiChatQuota');
  if (!el) return;
  if (!Number.isFinite(left)) { el.textContent = ''; return; }
  el.textContent = `נשארו ${left} שאלות היום`;
}
